import { FC, useCallback, useMemo } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import TabbedSubmitModal from 'dashboard/components/TabbedSubmitModal';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import LabeledFormFieldInput from 'dashboard/components-lib/LabeledFormFieldInput';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransactionStatus, TransactionStatusState } from 'dashboard/models/transaction-status';
import { TransactionType, TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useCreateTransactionMutation } from 'dashboard/redux/api/transaction';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { FieldSchemaType } from 'lib/fields';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface CreateTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoadingTransactions: boolean;
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
}

const CreateTransactionModal: FC<CreateTransactionModalProps> = ({
  isOpen,
  onClose,
  isLoadingTransactions,
  transactionStatuses,
  transactionTypes,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Form controls
  const {
    control,
    watch,
    handleSubmit: handleSubmitWrapper,
  } = useFormSecurely<FieldValues>({
    defaultValues: {
      transactionTypeId: undefined,
    },
  });
  const transactionTypeId = watch('transactionTypeId');

  // Submit mutation and handler
  const [createTransaction, { isLoading, error }] = useCreateTransactionMutation();
  useRTKErrorHandler({
    error,
    onError: (error) => ({
      message: 'Failed to create transaction:' + error.message,
      level: MessageType.Failure,
    }),
  });

  const hasTransactionViewPermission = usePermission(Permission.TransactionView);

  const handleSubmit = useCallback(
    async (args) => {
      const { fields, transactionTypeId, transactionStatusId, referenceId } = args;
      const response = await createTransaction({
        transactionTypeId,
        transactionStatusId,
        referenceId,
        fields,
      });
      dispatch(showMessage('Transaction created successfully', MessageType.Success));
      if (hasTransactionViewPermission && 'data' in response && response.data) {
        navigate(`/dashboard/transactions/${response.data.data.id}`);
      }
    },
    [createTransaction, hasTransactionViewPermission, dispatch, history],
  );

  // We only want active transaction type
  const activeTransactionTypes = transactionTypes.filter(
    (transactionType) => transactionType.attributes.status === TransactionTypeStatus.Active,
  );

  const selectedTxnType = activeTransactionTypes.find(
    (txnType) => txnType.id === transactionTypeId,
  );

  // We only want active statuses
  const activeTxnStatuses = useMemo(
    () =>
      transactionStatuses.filter(
        (status) =>
          status.relationships['transaction-type'].data.id === transactionTypeId &&
          status.attributes['status'] === TransactionStatusState.Active,
      ),
    [transactionTypeId, transactionStatuses],
  );

  const defaultTxnStatus = useMemo(
    () =>
      activeTxnStatuses.find(
        (status) => status.id === selectedTxnType?.relationships['default-status']?.data.id,
      ),
    [activeTxnStatuses, selectedTxnType],
  );

  const tabContents = [
    {
      name: 'Fields',
      contents: (
        <>
          {isLoadingTransactions ? (
            <LoadingIndicator />
          ) : activeTransactionTypes.length === 0 ? (
            'No transaction types available'
          ) : (
            <FormGroup>
              <FormControlLabel htmlFor='create-transaction-input__select-transaction-type-id'>
                Select Transaction Type
              </FormControlLabel>
              <Controller
                name='transactionTypeId'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    id='create-transaction-input__select-transaction-type-id'
                    placeholder='Select a transaction type'
                    disabled={isLoading}
                  >
                    {activeTransactionTypes.map(({ attributes, id }) => (
                      <Option key={`${attributes.name}-${id}`} value={id}>
                        {attributes.name}
                      </Option>
                    ))}
                  </Select>
                )}
              />

              {/* User cannot input anything until transaction type is selected */}
              {transactionTypeId && (
                <>
                  <Spacer height={2} />
                  <FormControlLabel htmlFor='create-transaction-input__transaction-reference-id'>
                    Transaction Reference ID
                  </FormControlLabel>
                  <Controller
                    name='referenceId'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id='create-transaction-input__transaction-reference-id'
                        placeholder='Enter a transaction reference ID (optional)'
                        disabled={isLoading}
                      />
                    )}
                  />

                  <Spacer height={2} />

                  <span>
                    <FormControlLabel htmlFor='create-transaction-input__select-transaction-status-id'>
                      Select Transaction Status
                    </FormControlLabel>
                    <HelpIcon>
                      If not specified, the transaction will be created with the default status
                      {defaultTxnStatus && `: (${defaultTxnStatus.attributes.name})`}
                    </HelpIcon>
                  </span>
                  <Controller
                    name='transactionStatusId'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        id='create-transaction-input__select-transaction-status-id'
                        placeholder='Select a transaction status (optional)'
                        disabled={isLoading}
                      >
                        {activeTxnStatuses.map(({ attributes, id }) => (
                          <Option key={`${attributes.name}-${id}`} value={id}>
                            {attributes.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                </>
              )}

              {selectedTxnType && (
                <>
                  <Spacer height={2} />
                  {selectedTxnType.attributes['field-schemas']
                    .filter(
                      (fieldSchema) =>
                        !fieldSchema.config['deactivated-at'] &&
                        fieldSchema.type !== FieldSchemaType.File,
                    )
                    .map((fieldSchema) => (
                      <LabeledFormFieldInput
                        key={fieldSchema.key}
                        fieldSchema={fieldSchema}
                        control={control}
                        disabled={isLoading}
                      />
                    ))}
                </>
              )}
            </FormGroup>
          )}
        </>
      ),
    },
  ];

  return (
    <ModalWrapper handleCloseModal={onClose} shouldCloseOnEsc isOpen={isOpen}>
      <TabbedSubmitModal
        tabContents={tabContents}
        isSubmitting={isLoading}
        disableSubmit={transactionTypeId == null}
        headerText='Create New Transaction'
        submitText='Create Transaction'
        handleCancel={onClose}
        handleSubmit={handleSubmitWrapper(handleSubmit)}
      />
    </ModalWrapper>
  );
};

export default CreateTransactionModal;
